import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnChanges,
    OnInit,
    OnDestroy,
    SimpleChanges,
    ChangeDetectorRef,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChannelTextInputComponent, InputValueState } from '../channel-text-input/channel-text-input.component';
import { Channel } from 'icreate/common/models/channel';
import { AlarmLevel } from 'icreate/common/models/alarm-level';
import { AlarmPropertyList } from 'icreate/common/models/repose-group';
import { ChannelType, ChannelTypeStringLookup, getRunningTypesSet } from 'icreate/common/models/channel-type';
import { ChannelSheetCol, motorDefinitionByTypeString } from '@models/channel-sheet';
import { ChannelSheetService } from '@service/channel/channel-sheet.service';
import { truncateToDecimals } from '@service/channel/channel-utils';

const MOTOR_PROPERTY_TO_COL: Partial<Record<string, ChannelSheetCol>> = {
    stopStatusString: ChannelSheetCol.STOP_STATUS,
    stbyStatusString: ChannelSheetCol.STBY_STATUS,
    runStatusString1: ChannelSheetCol.RUN1_STATUS,
    runStatusString2: ChannelSheetCol.RUN2_STATUS,
    runStatusString3: ChannelSheetCol.RUN3_STATUS,
    runStatusString4: ChannelSheetCol.RUN4_STATUS,
    runStatusString5: ChannelSheetCol.RUN5_STATUS,
};

@Component({
    selector: 'app-channel-alarm-form',
    templateUrl: './channel-alarm-form.component.html',
    styleUrls: ['./channel-alarm-form.component.scss'],
    standalone: false,
})
export class ChannelPanelAlarmFormComponent implements OnChanges, OnInit, OnDestroy {
    @Input() panelTitle: string = '';
    @Input() property: string = '';
    @Input() selectedChannel!: Channel;
    @Input() setPointOnly: boolean = false;
    @Input() toggleDisabled: boolean = false;

    @Output() onChannelChange = new EventEmitter();
    @Output() onFocusChange = new EventEmitter<void>();

    readonly ALARM_MESSAGE_MAX_LENGTH = 10;
    readonly MOTOR_STATUS_MAX_LENGTH = 10;

    isShowSelect = true;
    isAlarmEnabled: boolean | undefined;
    isDigitalMotor: boolean = false;
    isDigitalContact: boolean = false;
    isRunning: boolean = false;
    alarmLevel = AlarmLevel;
    alarmLevelOptions: string[] = [];
    alarmLevelSelectList: string[] = [];
    normalSetting: boolean | undefined = true;
    private saveStartedSubscription: Subscription;
    private validationEnabledSubscription: Subscription;

    @ViewChildren(ChannelTextInputComponent)
    textInputs: QueryList<ChannelTextInputComponent>;

    constructor(
        private cdr: ChangeDetectorRef,
        private channelSheetService: ChannelSheetService
    ) {}

    ngOnInit() {
        this.saveStartedSubscription = this.channelSheetService.saveStarted$.subscribe(() => {
            (document.activeElement as HTMLElement)?.blur();
            this.cdr.detectChanges();
        });
        this.validationEnabledSubscription = this.channelSheetService.validationEnabled$.subscribe(() => {
            this.textInputs?.forEach(input => input.revalidate());
        });
    }

    ngOnDestroy() {
        this.saveStartedSubscription?.unsubscribe();
        this.validationEnabledSubscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedChannel && this.selectedChannel) {
            this.initializeAlarmLevel();
            this.isAlarmEnabled = this.selectedChannel.fcuData[this.property]?.enable ?? false;

            const channelType: ChannelType =
                this.selectedChannel.fcuData?.channelType ??
                ChannelTypeStringLookup[this.selectedChannel.opsData?.channelType];
            this.isDigitalContact = channelType === ChannelType.DIGITAL_CONTACT;
            this.isDigitalMotor = channelType === ChannelType.DIGITAL_MOTOR;
            this.isRunning = getRunningTypesSet().has(channelType);

            if (this.isDigitalContact) {
                const normalOpenFlag = this.selectedChannel.fcuData.digitalAlarmSetting?.normalOpenFlag;
                this.normalSetting = !normalOpenFlag;
            }
            this.cdr.markForCheck();
        }
    }

    onInputChange(event: { name: string; value: any }) {
        if (event.name === 'alarmLevel') {
            const numericValue = AlarmLevel[event.value as keyof typeof AlarmLevel];
            this.onChannelChange.emit({
                name: 'alarmLevel',
                value: numericValue,
                property: this.property,
            });
            return;
        }
        if (event.name === 'setPoint') {
            let inputValue = String(event.value ?? '');

            if (this.isRunning) {
                const correctedValue = this.correctRunningHourSetPoint(inputValue);
                if (correctedValue !== inputValue) {
                    event.value = correctedValue;
                    this.cdr.detectChanges();
                }
            }
        }
        this.onChannelChange.emit({ ...event, property: this.property });
    }

    openSelect(event: MouseEvent) {
        event.stopPropagation();
        this.isShowSelect = !this.isShowSelect;
    }

    updateAlarmEnable(event: any) {
        const checked = event.checked;
        if (this.selectedChannel?.fcuData?.[this.property]) {
            this.onChannelChange.emit({
                name: 'enable',
                value: checked,
                property: this.property,
            });
        }
        this.isAlarmEnabled = checked;
    }

    numberValidation(value: string) {
        const isEmpty = value === null || value === undefined || String(value).trim() === '';

        if (isEmpty) {
            return {
                state: InputValueState.error,
                message: 'input.text.errors.REQUIRED',
            };
        }

        if (!/^-?\d+(\.\d+)?$/.test(value)) {
            return {
                state: InputValueState.error,
                message: 'input-text.errors.NOT_NUMBER_TYPE',
            };
        }

        return {
            state: InputValueState.init,
            message: '',
        };
    }

    mrepValidation(value: string) {
        if (value !== '1' && value !== '0') {
            return {
                state: InputValueState.error,
                message: 'input-text.errors.NOT_BINARY_NUMBER',
            };
        } else {
            return this.numberValidation(value);
        }
    }

    positiveWholeNumberValidation(value: string) {
        const isEmpty = value === null || value === undefined || String(value).trim() === '';

        if (isEmpty) {
            if (!this.channelSheetService.getIsValidationEnabled()) {
                return {
                    state: InputValueState.init,
                    message: '',
                };
            }
            return {
                state: InputValueState.error,
                message: 'input.text.errors.REQUIRED',
            };
        }

        if (!/^\d+$/.test(value)) {
            return {
                state: InputValueState.error,
                message: 'input-text.errors.NOT_INTEGER',
            };
        }

        return {
            state: InputValueState.init,
            message: '',
        };
    }

    integerOnlyKeyDown(event: KeyboardEvent) {
        const allowedKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
        ];
        if (allowedKeys.includes(event.key)) return;
        if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key)) return;
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    onNormalSettingChange(value: boolean) {
        this.onChannelChange.emit({
            name: 'normalOpenFlag',
            value: !value,
            property: this.property,
        });
    }

    handleFocusChanged() {
        this.cdr.detectChanges();
        this.onFocusChange.emit();
    }

    requiredValidation(value: any) {
        if (!this.isRequiredField) {
            return {
                state: InputValueState.init,
                message: '',
            };
        }

        if (value === null || value === undefined || String(value).trim() === '') {
            return {
                state: InputValueState.error,
                message: 'input.text.errors.REQUIRED',
            };
        }
        return {
            state: InputValueState.init,
            message: '',
        };
    }

    get isRequiredField(): boolean {
        if (!this.selectedChannel || !this.isDigitalMotor) {
            return false;
        }

        const motorType = this.selectedChannel.fcuData?.motorChannelSetting?.motorType;

        const col = MOTOR_PROPERTY_TO_COL[this.property];

        return !!(motorType && col !== undefined && motorDefinitionByTypeString.get(motorType)?.[col] !== '');
    }

    formatSetPoint(property: string): string {
        const isSettingEnabled = this.selectedChannel?.fcuData?.[property].enable;
        const value = Number(this.selectedChannel?.fcuData?.[property]?.setPoint);
        return isSettingEnabled ? (Number.isFinite(value) ? this.toScaledString(value) : '') : '';
    }

    private toScaledString(value: number): string {
        const scale = this.selectedChannel?.fcuData?.scale || 1;

        const decimals = this.isRunning ? 2 : scale > 1 ? Math.round(Math.log10(scale)) : 0;

        return truncateToDecimals(value, decimals);
    }

    private correctRunningHourSetPoint(value: string): string {
        const decimal = value.split('.')[1];

        return decimal && Number(decimal) >= 60 ? value.split('.')[0] : value;
    }

    private initializeAlarmLevel() {
        this.alarmLevelOptions = Object.keys(AlarmLevel).filter(key => isNaN(+key) && key !== 'None');

        this.alarmLevelSelectList =
            this.property === AlarmPropertyList.alarmSettingSensorFail ? ['Alarm'] : [...this.alarmLevelOptions];
    }
}
